import ClickAwayListener from '@mui/material/ClickAwayListener'
import Popper from '@mui/material/Popper'
import Button from 'ext-ui/components/Button/Button'
import CloseButton from 'ext-ui/components/CloseButton/CloseButton'
import TextAreaInput, {
  TextAreaInputRef,
} from 'ext-ui/components/TextAreaInput/TextAreaInput'
import AiStarsIconV2 from 'ext-ui/svg/icon/AiStarsIconV2'
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import extendMark from 'src/assets/images/Extend-Mark-Colored.svg'
import { useTranslate } from 'src/localization/Trns'

import * as style from './ChartQuestionPopover.style'

export interface ChartQuestionPopoverProps {
  chartTitle: string
  contextLabel: string
  onSubmit: (question: string) => void
}

const ChartQuestionPopover: React.FC<ChartQuestionPopoverProps> = ({
  chartTitle,
  contextLabel,
  onSubmit,
}) => {
  const translate = useTranslate()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<TextAreaInputRef | null>(null)
  const dialogId = useId()
  const [open, setOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const dialogLabel = translate('Ask Reporting Agent about ${chartTitle}', {
    chartTitle,
  })
  const setInputRef = useCallback(
    (value: TextAreaInputRef | null): void => {
      inputRef.current = value
      if (open) value?.focus()
    },
    [open],
  )

  useEffect(() => {
    if (!open) return undefined
    inputRef.current?.focus()
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  const close = (): void => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const submit = (): void => {
    const trimmed = question.trim()
    if (trimmed.length === 0) return
    onSubmit(trimmed)
    setQuestion('')
    setOpen(false)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        css={style.trigger}
        aria-label={dialogLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dialogId}
        onClick={(): void => setOpen((current) => !current)}
      >
        <AiStarsIconV2 width={20} height={20} />
      </button>
      <Popper
        open={open}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        css={style.popper}
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 16 } },
        ]}
      >
        <ClickAwayListener onClickAway={(): void => setOpen(false)}>
          <div
            id={dialogId}
            role="dialog"
            aria-label={dialogLabel}
            css={style.dialog}
          >
            <header css={style.header}>
              <span css={style.agentIcon} aria-hidden="true">
                <img src={extendMark} alt="" css={style.agentMark} />
                <span css={style.agentBadge}>
                  <AiStarsIconV2 width={14} height={14} />
                </span>
              </span>
              <h3 css={style.title}>{translate('Reporting Agent')}</h3>
              <CloseButton
                variant="dark-on-light"
                css={style.closeButton}
                aria-label={translate('Close')}
                onClick={close}
              />
            </header>
            <p css={style.context}>{contextLabel}</p>
            <div css={style.composer}>
              <TextAreaInput
                ref={setInputRef}
                css={style.input}
                variant="small"
                autoResize
                value={question}
                aria-label={translate('Ask about ${chartTitle}', {
                  chartTitle,
                })}
                placeholder={translate('Ask a question about this chart')}
                onChange={(event): void => setQuestion(event.target.value)}
                onKeyDown={(event): void => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    submit()
                  }
                }}
              />
              <Button
                type="button"
                css={style.sendButton}
                dropShadow={false}
                disabled={question.trim().length === 0}
                onClick={submit}
              >
                {translate('Send')}
              </Button>
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  )
}

export default ChartQuestionPopover
